import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useTeam } from "@/lib/team";
import teamGroup from "@/assets/team/team-group.png";
import heroOffice from "@/assets/hero-office.webp";
import { ArrowRight, Check } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";

export const Route = createFileRoute("/about/equipo")({
  component: Equipo,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Equipo — RR Lex Juristas" },
      { name: "description", content: "Conoce a Charlene Ramos Rodrigues y al equipo de RR Lex Juristas: abogacía humana, transparente y comprometida." },
      { property: "og:title", content: "El equipo de RR Lex Juristas" },
      { property: "og:description", content: "Charlene Ramos Rodrigues lidera un equipo multidisciplinar en Sevilla." },
      { property: "og:url", content: "https://rrlexjuristas.com/about/equipo" },
      { property: "og:image", content: teamGroup },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/about/equipo" }],
  }))(ctx)),
});

function Equipo() {
  const { tk } = useT();
  const heroBg = useMedia("home.office.hero", heroOffice, "Despacho RR Lex Juristas");
  const group = useMedia("home.team.group", teamGroup, "Equipo completo de RR Lex Juristas");
  const team = useTeam();

  return (
    <Layout>
      <section className="relative border-b border-gold/15">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${group.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("equipo.kicker", "Nuestro Equipo")}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-4xl mx-auto">
            {tk("equipo.title.a", "El talento detrás de ")}
            <em className="not-italic gold-gradient">RR Lex Juristas</em>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk(
              "equipo.intro",
              "Un equipo multidisciplinar de abogados y asesores que combina rigor técnico, vocación y un trato profundamente humano. Liderado por Charlene Ramos Rodrigues, una de las mejores penalistas de España.",
            )}
          </p>
        </div>
      </section>

      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="relative rounded-2xl overflow-hidden border border-gold/20">
            <img src={group.url} alt={group.alt} className="w-full h-auto object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="text-[10px] tracking-[0.35em] uppercase text-gold/90">
                {tk("equipo.group.kicker", "Despacho Sevilla & Camas")}
              </div>
              <div className="font-serif text-2xl md:text-4xl mt-2 max-w-3xl">
                {tk(
                  "equipo.group.line",
                  "Seis profesionales unidos por una misma vocación: defender tus derechos con cercanía y firmeza.",
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 space-y-24">
        {team.map((m, idx) => (
          <article
            key={m.slug}
            id={m.slug}
            className="scroll-mt-32 grid md:grid-cols-5 gap-12 items-center"
          >
            <div className={`md:col-span-2 relative ${idx % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="absolute -inset-3 rounded-2xl border border-gold/25" />
              <div className="relative rounded-xl overflow-hidden bg-card border border-gold/20 aspect-[3/4]">
                <img
                  src={m.portrait.url}
                  alt={m.portrait.alt || m.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              {m.featured && (
                <div className="absolute -bottom-5 -right-5 hidden md:block rounded-xl border border-gold/40 bg-background p-4 w-44 text-center">
                  <div className="font-serif text-2xl gold-gradient">+3000</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">
                    {tk("equipo.cases", "Casos resueltos")}
                  </div>
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <span className="text-xs tracking-[0.4em] uppercase text-gold">
                {m.featured ? tk("equipo.lead", "Fundadora & Directora") : tk("equipo.member", "Equipo")}
              </span>
              <h2 className="font-serif text-3xl md:text-5xl mt-3">{m.name}</h2>
              <div className="text-sm text-foreground/70 mt-3">{m.role}</div>
              <div className="gold-divider my-6 max-w-[100px]" />
              <p className="text-muted-foreground leading-relaxed">{m.bio}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {m.specialties.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-foreground/80">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </section>

      <section className="relative border-y border-gold/20 bg-cover bg-center" style={{ backgroundImage: `url(${heroBg.url})` }}>
        <div className="absolute inset-0 bg-background/80" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("equipo.cta.kicker", "¿Hablamos de tu caso?")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">
            {tk("equipo.cta.title", "Reserva una consulta con nuestro equipo")}
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            {tk(
              "equipo.cta.text",
              "Te escuchamos, analizamos tu situación y te orientamos sobre los próximos pasos con total transparencia.",
            )}
          </p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold text-primary-foreground px-7 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
          >
            {tk("equipo.cta.button", "Reservar consulta")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
